import {
  FileKey,
  Camera,
  Banknote,
  Users,
  Briefcase,
  FileText,
  IdCard,
  Car,
} from "lucide-react";

// ─── Shared sections (reused across financiers) ──────────────────────────────

const coApplicant = {
  id: "co-applicant",
  category: "Co-Applicant / Guarantor",
  subtext: "(If CIBIL < 650 or Age Exception)",
  icon: Users,
  documents: [
    {
      id: "co-aadhaar",
      name: "Co-Applicant Aadhaar",
      uploadType: "both",
      requirementType: "CONDITIONAL",
      rule: {
        anyOf: [
          { field: "cibilScore", lessThan: 650 },
          { field: "ageException", equals: true },
        ],
      },
    },
    {
      id: "co-pan",
      name: "Co-Applicant PAN",
      uploadType: "single",
      requirementType: "CONDITIONAL",
      rule: {
        anyOf: [
          { field: "cibilScore", lessThan: 650 },
          { field: "ageException", equals: true },
        ],
      },
    },
    {
      id: "co-affidavit",
      name: "Affidavit",
      uploadType: "single",
      requirementType: "CONDITIONAL",
      rule: {
        anyOf: [
          { field: "cibilScore", lessThan: 650 },
          { field: "ageException", equals: true },
        ],
      },
    },
    {
      id: "co-cheques",
      name: "Signed Cheques",
      uploadType: "multiple",
      requirementType: "CONDITIONAL",
      rule: {
        anyOf: [
          { field: "cibilScore", lessThan: 650 },
          { field: "ageException", equals: true },
        ],
      },
    },
    {
      id: "co-kyc",
      name: "Complete KYC",
      uploadType: "multiple",
      requirementType: "CONDITIONAL",
      rule: {
        anyOf: [
          { field: "cibilScore", lessThan: 650 },
          { field: "ageException", equals: true },
        ],
      },
    },
  ],
};

const coApplicantUnconditional = {
  id: "co-applicant-unconditional",
  category: "Co-Applicant / Guarantor",
  icon: Users,
  documents: [
    {
      id: "co-unconditional-aadhaar",
      name: "Co-Applicant Aadhaar",
      uploadType: "both",
      requirementType: "OPTIONAL",
    },
    {
      id: "co-unconditional-pan",
      name: "Co-Applicant PAN",
      uploadType: "single",
      requirementType: "OPTIONAL",
    },
  ],
};

const additional = {
  id: "additional",
  category: "Additional",
  icon: Briefcase,
  documents: [
    {
      id: "noc",
      name: "NOC / Loan Statement",
      checklistLabel: "NOC / Loan Statement (If vehicle is financed)",
      uploadType: "single",
      requirementType: "CONDITIONAL",
      rule: { field: "vehicleFinanced", equals: "Yes" },
    },
  ],
};

const others = {
  id: "others",
  category: "Other Documents",
  icon: Briefcase,
  documents: [
    {
      id: "others",
      name: "Add Multiple Documents",
      uploadType: "multiple",
      requirementType: "OPTIONAL",
    },
  ],
};

// ─── Financier document configs ───────────────────────────────────────────────

export const LTO_DOCUMENTS = [
  {
    id: "lto-identity",
    category: "Identity & Address",
    icon: FileKey,
    documents: [
      {
        id: "aadhaar",
        name: "Aadhaar (Front / Back)",
        uploadType: "both",
        requirementType: "MANDATORY",
      },
      {
        id: "pan",
        name: "PAN",
        uploadType: "single",
        requirementType: "MANDATORY",
      },
      {
        id: "rc",
        name: "RC (Front / Back)",
        uploadType: "both",
        requirementType: "MANDATORY",
      },
      {
        id: "passbook",
        name: "Bank Passbook",
        uploadType: "multiple",
        requirementType: "MANDATORY",
      },
      {
        id: "residency",
        name: "Residency Proof (Electricity / Rent / Ration / Gas / Water)",
        checklistLabel:
          "Residency Proof (Any One): Electricity Bill / Rent Agreement / Ration Card / Gas Passbook / Water Bill",
        uploadType: "single",
        requirementType: "MANDATORY",
        onlyOneRequired: true,
      },
    ],
  },
  {
    id: "lto-verification",
    category: "Verification Media",
    icon: Camera,
    documents: [
      {
        id: "photo-vehicle",
        name: "Photo with Vehicle",
        uploadType: "single",
        requirementType: "MANDATORY",
      },
      {
        id: "photo-borrower",
        name: "Borrower / Driver with House Photo",
        uploadType: "single",
        requirementType: "MANDATORY",
      },
      {
        id: "house-photos",
        name: "House Photos (Multiple)",
        uploadType: "multiple",
        requirementType: "MANDATORY",
      },
      {
        id: "video-verify",
        name: "Video Verification",
        uploadType: "single",
        requirementType: "MANDATORY",
      },
      {
        id: "map-location",
        name: "Google Map Location (Link)",
        uploadType: "single",
        requirementType: "OPTIONAL",
      },
    ],
  },
  {
    id: "lto-banking",
    category: "Banking",
    icon: Banknote,
    documents: [
      {
        id: "cheques",
        name: "Cheques (2 Signed)",
        checklistLabel:
          "Cheques (2 signed) — except Co-Applicant cases per CIBIL",
        uploadType: "multiple",
        requirementType: "MANDATORY",
      },
    ],
  },
  coApplicant,
  additional,
  others,
];

export const AMU_DOCUMENTS = [
  {
    id: "amu-loan",
    category: "Loan Application Docs",
    icon: FileText,
    documents: [
      {
        id: "loan-app",
        name: "Loan Application – 4 Pages",
        uploadType: "single",
        requirementType: "MANDATORY",
      },
      {
        id: "agreement",
        name: "Application Form / Agreement – 34 Pages",
        uploadType: "multiple",
        requirementType: "MANDATORY",
      },
      {
        id: "consent",
        name: "Consent Letter",
        uploadType: "single",
        requirementType: "MANDATORY",
      },
      {
        id: "declaration",
        name: "Declaration Form",
        uploadType: "single",
        requirementType: "MANDATORY",
      },
      {
        id: "fi-tvr",
        name: "FI – TVR Form",
        uploadType: "single",
        requirementType: "MANDATORY",
      },
      {
        id: "form-60",
        name: "Form No. 60",
        uploadType: "single",
        requirementType: "OPTIONAL",
      },
    ],
  },
  {
    id: "amu-kyc",
    category: "KYC PDFs",
    icon: IdCard,
    documents: [
      {
        id: "combined-kyc",
        name: "Combined KYC (Aadhaar + PAN + RC + Electricity Bill + Passbook)",
        uploadType: "multiple",
        requirementType: "MANDATORY",
      },
    ],
  },
  {
    id: "amu-banking",
    category: "Banking",
    icon: Banknote,
    documents: [
      {
        id: "cheques",
        name: "Mandate / Cheques",
        checklistLabel: "NACH Sign OR Cheque 1 / Cheque 2 / Cheque 3",
        uploadType: "multiple",
        requirementType: "MANDATORY",
        onlyOneRequired: true,
      },
    ],
  },
  {
    id: "amu-photos",
    category: "Photos & Location",
    icon: Camera,
    documents: [
      {
        id: "house-location",
        name: "House Photos & Location",
        uploadType: "multiple",
        requirementType: "MANDATORY",
      },
    ],
  },
  coApplicant,
  additional,
  others,
];

export const MOOVING_DOCUMENTS = [
  {
    id: "mooving-driver",
    category: "Driver & Vehicle Details",
    icon: Car,
    documents: [
      {
        id: "driver-vehicle",
        name: "Driver & Vehicle Details",
        checklistLabel:
          "Driver Name, Phone, Alt Phone, Address + Vehicle RC + Financed (Yes/No)",
        uploadType: "single",
        requirementType: "MANDATORY",
      },
    ],
  },
  {
    id: "mooving-ownership",
    category: "Ownership",
    icon: Briefcase,
    documents: [
      {
        id: "ownership-details",
        name: "Ownership Details",
        uploadType: "single",
        requirementType: "OPTIONAL",
      },
      {
        id: "purchase-agreement",
        name: "Purchase Agreement",
        uploadType: "single",
        requirementType: "OPTIONAL",
      },
      // {
      //   id: "sale-letter",
      //   name: "Sale Letter",
      //   checklistLabel: "Sale Letter (Mandatory if second owner)",
      //   uploadType: "single",
      //   requirementType: "CONDITIONAL",
      //   rule: { field: "secondOwner", equals: true },
      // },
    ],
  },
  {
    id: "mooving-kyc",
    category: "KYC",
    icon: IdCard,
    documents: [
      {
        id: "combined-kyc",
        name: "Combined KYC (Aadhaar + PAN + Electricity Bill)",
        uploadType: "single",
        requirementType: "MANDATORY",
      },
      {
        id: "rc",
        name: "RC (Registration Certificate)",
        uploadType: "both",
        requirementType: "MANDATORY",
      },
    ],
  },
  {
    id: "mooving-banking",
    category: "Banking",
    icon: Banknote,
    documents: [
      {
        id: "cheques-3",
        name: "Cheques (3) – Combined PDF",
        uploadType: "single",
        requirementType: "MANDATORY",
      },
    ],
  },
  {
    id: "mooving-photos",
    category: "Photos & Location",
    icon: Camera,
    documents: [
      {
        id: "house-photos-location",
        name: "House Photos & Location",
        checklistLabel:
          "House Photos + Photo with Vehicle + Borrower with House Photo + Google Map Location",
        uploadType: "multiple",
        requirementType: "MANDATORY",
      },
    ],
  },
  coApplicant,
  additional,
  others,
];

export const FINAYO_DOCUMENTS = [
  {
    id: "finayo-driver-vehicle",
    category: "Driver & Vehicle",
    icon: Car,
    documents: [
      {
        id: "pan",
        name: "PAN Card",
        uploadType: "single",
        requirementType: "MANDATORY",
      },
      {
        id: "aadhaar",
        name: "Aadhaar Card (Front & Back)",
        uploadType: "both",
        requirementType: "MANDATORY",
      },
      {
        id: "rc",
        name: "RC (Non-HYP)",
        uploadType: "both",
        requirementType: "MANDATORY",
      },
      {
        id: "electricity-bill",
        name: "Electricity Bill",
        uploadType: "single",
        requirementType: "MANDATORY",
      },
    ],
  },

  {
    id: "finayo-financier-details",
    category: "Financier Details",
    icon: Briefcase,
    documents: [
      {
        id: "noc-loan-aggregator",
        name: "NOC from Earlier Loan Aggregator",
        uploadType: "single",
        requirementType: "CONDITIONAL",
        checklistLabel: "NOC / Loan Statement (If vehicle is financed)",
        rule: { field: "vehicleFinanced", equals: "Yes" },
      },
    ],
  },

  {
    id: "finayo-kyc-banking",
    category: "KYC & Banking",
    icon: Banknote,
    documents: [
      {
        id: "bank-history",
        name: "Banking History / Account Aggregator",
        subtext: "6 months bank statement or Account Aggregator report",
        uploadType: "multiple",
        requirementType: "MANDATORY",
      },
      {
        id: "nach-registration",
        name: "NACH Registration",
        subtext: "Signed mandate form",
        uploadType: "single",
        requirementType: "MANDATORY",
      },
    ],
  },

  {
    id: "finayo-house-verification",
    category: "House Verification",
    icon: Camera,
    documents: [
      {
        id: "house-photos",
        name: "House Photos",
        uploadType: "multiple",
        requirementType: "MANDATORY",
        checklistLabel:
          "House Photos + Photo with Vehicle + Borrower with House Photo + Google Map Location",
      },
    ],
  },

  coApplicantUnconditional,
  additional,
  others,
];

// ─── Lookup map (replaces FINANCIER_TYPES + DOCUMENT_DATA) ───────────────────

export const FINANCIER_TYPES = {
  // LTO: "LTO",
  AMU: "AMU",
  LTO_LOW_CIBIL: "LTO_LOW_CIBIL",
  BAJAJ: "BAJAJ",
  LTO_BAJAJ: "LTO_BAJAJ",
  LTO_AMU: "LTO_AMU",
  VN_CREDITS: "VN_CREDITS",
  LTO_VN_CREDITS: "LTO_VN_CREDITS",
};

export const DOCUMENTS_BY_FINANCIER = {
  // LTO: LTO_DOCUMENTS,
  AMU: AMU_DOCUMENTS,
  BAJAJ: LTO_DOCUMENTS,
  LTO_BAJAJ: LTO_DOCUMENTS,
  LTO_AMU: LTO_DOCUMENTS,
  VN_CREDITS: LTO_DOCUMENTS,
  LTO_VN_CREDITS: LTO_DOCUMENTS,
  LTO_LOW_CIBIL: LTO_DOCUMENTS,
};
